'use client';
import Image from 'next/image';
import React from 'react';
import Button from '../ui/button';
import { usePostHogEvents } from '@/hooks/usePostHogEvents';
import { getThemeClass } from '@/helpers/theme';
import { cn } from '@/lib/utils';

interface SingleBanner {
  title: string;
  desc: string;
  image?: string;
  mobile_image?: string;
  imageAlt?: string;
  mobileImageAlt?: string;
  btn_text: string;
  btn_url: string;
  btn_color: string;
  has_cta: boolean;
  theme?: 'dark' | 'light';
  className?: string;
  contentClassName?: string;
  headingClassName?: string;
  descriptionClassName?: string;
  headingAs?: 'h1' | 'h2' | 'h3';
  children?: React.ReactNode;
}

const SingleBanner = ({
  title,
  desc,
  image,
  mobile_image,
  imageAlt,
  mobileImageAlt,
  btn_text,
  btn_url,
  btn_color,
  has_cta,
  theme = 'dark',
  className,
  contentClassName,
  headingClassName,
  descriptionClassName,
  headingAs: Heading = 'h1',
  children,
}: SingleBanner) => {
  const { sendButtonClickEvent } = usePostHogEvents();
  const isExternal =
    /^(https?:)?\/\//i.test(btn_url?.trim() ?? '') &&
    !/^(https?:)?\/\/(www\.)?unimig\.(com|com\.au)/i.test(
      btn_url?.trim() ?? ''
    );

  return (
    <div
      className={cn(
        'h-[600px] md:h-[750px] lg:rounded-[10px] overflow-hidden w-full flex flex-col md:justify-start justify-center items-center relative',
        className
      )}
    >
      {mobile_image ? (
        <>
          <Image
            src={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/${mobile_image}`}
            alt={mobileImageAlt ?? imageAlt ?? title}
            title={title}
            fill
            loading="lazy"
            sizes="(max-width: 639px) 100vw, 0px"
            className="w-full h-full object-cover select-none block sm:hidden"
          />
          <Image
            src={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/${image}`}
            alt={imageAlt ?? title}
            title={title}
            fill
            loading="lazy"
            sizes="(max-width: 639px) 0px, (max-width: 1200px) 100vw, 1280px"
            className="w-full h-full object-cover select-none hidden sm:block"
          />
        </>
      ) : image ? (
        <Image
          src={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/${image}`}
          alt={imageAlt ?? title}
          title={title}
          fill
          loading="lazy"
          sizes="(max-width: 1200px) 100vw, 1280px"
          className="w-full h-full object-cover select-none"
        />
      ) : null}

      <div
        className={cn(
          'w-full h-full justify-between md:justify-start z-10 px-4 text-center flex flex-col gap-3 lg:gap-3 items-center md:pt-24 pt-14 pb-14 md:pb-16 transition-transform slider-content',
          contentClassName
        )}
      >
        <span className="flex flex-col gap-3">
          <Heading
            className={cn(
              `font-bold ${getThemeClass(theme, 'heading')} text-2xl leading-[32px] md:text-[32px] md:leading-[48px]`,
              headingClassName
            )}
          >
            {title}
          </Heading>
          <p
            className={cn(
              `md:text-[18px] text-[16px] font-proxima_font ${getThemeClass(theme, 'text')}`,
              descriptionClassName
            )}
          >
            {desc}
          </p>
        </span>
        {has_cta && (
          <Button
            onClick={() => {
              sendButtonClickEvent(btn_text, title);
            }}
            label={(btn_text + '>') as string}
            color={btn_color ?? 'white'}
            to={btn_url ?? '/'}
            newTab={isExternal}
            className="text-[14px] font-semibold mt-4 font-proxima_font"
          />
        )}
        {children}
      </div>
    </div>
  );
};

export default SingleBanner;
